'use client';

import { MediaQuery } from '@constants/theme';
import MuiBox from '@new-components/atoms/MuiBox';
import MuiButton from '@new-components/atoms/MuiButton';
import MuiContainer from '@new-components/atoms/MuiContainer';
import MuiTypography from '@new-components/atoms/MuiTypography';
import { useLeadForm } from '@new-components/templates/LeadGeneration/provider';
import { IconMap } from '@new-components/templates/SearchBox/useSearchState';
import { trackMouseClicks } from '@utils/googleAnalytics/googleAnalyticsEvents';

const MailIcon = () => {
  return (
    <svg
      width='20'
      height='20'
      viewBox='0 0 20 20'
      fill='none'
      xmlns='http://www.w3.org/2000/svg'>
      <path
        d='M1.66699 5.83325L8.47109 10.5961C9.02207 10.9818 9.29756 11.1746 9.59721 11.2493C9.8619 11.3153 10.1387 11.3153 10.4034 11.2493C10.7031 11.1746 10.9786 10.9818 11.5296 10.5961L18.3337 5.83325M5.66699 16.6666H14.3337C15.7338 16.6666 16.4339 16.6666 16.9686 16.3941C17.439 16.1544 17.8215 15.772 18.0612 15.3016C18.3337 14.7668 18.3337 14.0667 18.3337 12.6666V7.33325C18.3337 5.93312 18.3337 5.23306 18.0612 4.69828C17.8215 4.22787 17.439 3.84542 16.9686 3.60574C16.4339 3.33325 15.7338 3.33325 14.3337 3.33325H5.66699C4.26686 3.33325 3.5668 3.33325 3.03202 3.60574C2.56161 3.84542 2.17916 4.22787 1.93948 4.69828C1.66699 5.23306 1.66699 5.93312 1.66699 7.33325V12.6666C1.66699 14.0667 1.66699 14.7668 1.93948 15.3016C2.17916 15.772 2.56161 16.1544 3.03202 16.3941C3.5668 16.6666 4.26686 16.6666 5.66699 16.6666Z'
        stroke='rgba(0, 50, 67, 1)'
        strokeWidth='1.66667'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    </svg>
  );
};

export type Props = {
  communityName: string;
  totalUnits: string;
  paymentPlan: string;
  sizeRange: string;
  propertyTypes: UnitCardType[];
  lastUpdated: string;
  completionStatus: string;
  completionDate: string;
  availableUnits: string;
  unitTypes: string;
  communityId: string;
  brandType: 'aldar' | 'lsq';
};
export function CommunityDetailsSpecifications(props: Props) {
  const { show } = useLeadForm();
  const handleEnquireClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (props.brandType === 'aldar')
      return show({
        formType: 'COMMUNITY_FORM',
        id: props.communityId,
      });
    show({
      formType: 'LSQ_COMMUNITY_FORM',
      id: props.communityId,
    });
    trackMouseClicks({type: 'button', label: 'register_interest'});
  }
  return (
    <MuiContainer id='comm-specifications'>
      <MuiBox sx={{ display: "flex", justifyContent: "space-between", alignItems: { xs:'center',md: 'flex-end' }, marginBottom: '32px', flexDirection: { xs: "column", md: "row" }, gap: { xs: "32px", md: "0px" } }}>
        <MuiBox sx={{ display: "flex", flexDirection: "column", width:{xs:"100%",md:'auto'} }}>
          <MuiTypography
            sx={{
              fontWeight: { xs: 400, md: 300 },
              fontSize: { xs: '24px', md: '48px' },
              lineHeight: { xs: '30px', md: '60px' },
              letterSpacing: { xs: '-0.4px', md: '-0.8px' },
              color: '#003243',
              marginBottom: { xs: "8px", md: "16px" },
            }}>
            {props.communityName}
            {' at a Glance'}
          </MuiTypography>
          {<MuiTypography
            sx={{
              fontWeight: 400,
              fontSize: { xs: '16px', md: '22px' },
              lineHeight: '125%',
              letterSpacing: '-0.4px',
              color: '#003243',
              opacity: .6,
            }}>
            {props.lastUpdated ? `As of ${props.lastUpdated}` : null}
          </MuiTypography>}
        </MuiBox>

        <MuiButton
          onClick={handleEnquireClick}
          variant='outlined'
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '10px 16px',
            borderRadius: '24px',
            borderColor: 'rgba(0, 50, 67, 1)',
            color: 'rgba(0, 50, 67, 1)',
            width: '100%',
            maxWidth: '250px',
            height: 'fit-content'
          }}>
          <MailIcon />
          Enquire
        </MuiButton>
      </MuiBox>
      <MuiBox
        sx={{
          display: 'flex',
          gap: '24px',
          [MediaQuery.mobile]: {
            flexDirection: 'column',
            gap: '16px',
          },
        }}>
        <MuiBox
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
            flex: '1 1 auto',
            [MediaQuery.desktop]: {
              maxWidth: '362px',
              ['& > :nth-of-type(1)']: {
                flex: '1',
              },
            },
            paddingBottom: '8px',
            [MediaQuery.mobile]: {
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gridTemplateRows: 'auto auto',
              gridRow: '1',
              ['& > :nth-of-type(3)']: {
                gridRow: '2',
                gridColumn: 'span 2',
              },
            },
          }}>

          <StatsCard value={props.availableUnits} label='Unit for Sale' highlight/>
          <StatsCard value={props.completionDate} label='Completion Date' />
          <StatsCard value={props.sizeRange} label='Property Size Range' />
        </MuiBox>
        <MuiBox
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
            flex: '1 1 auto',
            [MediaQuery.desktop]: {
              maxWidth: '362px',
              ['& > :nth-of-type(1)']: {
                flex: '1',
              },
            },
            paddingBottom: '8px',
            [MediaQuery.mobile]: {
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gridTemplateRows: 'auto auto',
              gridRow: '1',
              ['& > :nth-of-type(3)']: {
                gridRow: '2',
                gridColumn: 'span 2',
              },
            },
          }}>
          <StatsCard
            value={`${props.totalUnits}`}
            label='Total Units'
            highlight
          />
          <StatsCard value={props.completionStatus} label='Status' />
          <StatsCard value={props.unitTypes} label='Unit Types' />
        </MuiBox>
        <MuiBox
          sx={{
            display: 'flex',
            gap: '16px',
            overflow: 'auto',
            paddingBottom: '8px',
            '&::-webkit-scrollbar': {
              display: 'none',
            },
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            [MediaQuery.mobile]: {
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gridTemplateRows: 'auto auto',
              gridRow: '1',
            },
          }}>
          {props.propertyTypes.map((data) => (
            <UnitTypeCard {...data} />
          ))}
        </MuiBox>
      </MuiBox>
    </MuiContainer>
  );
}

function StatsCard(props: { value: string; label: string; highlight?: true }) {
  return (
    <MuiBox
      sx={{
        background: '#FFFFFF80',
        borderRadius: '24px',
        // backdropFilter: 'blur(80px)',
        // backdropFilter: 'blur(80px)',
        padding: '24px 46px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '4px',
        whiteSpace: 'nowrap',
        [MediaQuery.mobile]: {
          padding: '16px',
          borderRadius: '24px',
        },
      }}>
      <MuiTypography
        sx={{
          textAlign: 'center',
          color: '#2D2D2D',
          ...(props.highlight
            ? {
              fontWeight: 300,
              fontSize: '48px',
              lineHeight: '52.8px',
              letterSpacing: '-0.8px',
            }
            : { fontWeight: 500, fontSize: '24px', letterSpacing: '-0.4px' }),
          [MediaQuery.mobile]: {
            fontWeight: 400,
            fontSize: '17px',
            lineHeight: '23.75px',
            letterSpacing: '-0.4px',
          },
        }}>
        {props.value}
      </MuiTypography>
      <MuiTypography
        sx={{
          fontWeight: 400,
          letterSpacing: '-0.4px',
          textAlign: 'center',
          color: '#2D2D2D',
          ...(props.highlight
            ? { fontSize: '19px', lineHeight: '23.75px' }
            : { fontSize: '17px', lineHeight: '23.8px' }),

          [MediaQuery.mobile]: {
            fontWeight: 400,
            fontSize: '12px',
            lineHeight: '16.25px',
            letterSpacing: 0,
            textAlign: 'center',
          },
        }}>
        {props.label}
      </MuiTypography>
    </MuiBox>
  );
}

type UnitCardType = {
  name: string;
  bedrooms: string;
  iconType: keyof typeof IconMap;
};
function UnitTypeCard(props: UnitCardType) {
  return (
    <MuiBox
      sx={{
        padding: '24px 24px',
        borderRadius: '120px 120px 16px 16px',
        background: '#FFFFFF80',
        // backdropFilter: 'blur(80px)',
        // backdropFilter: 'blur(80px)',
        display: 'flex',
        [MediaQuery.desktop]: {
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          minWidth:'200px',
          maxWidth:'200px'
        },
        [MediaQuery.mobile]: {
          borderRadius: '16px',
          padding: '16px',
          flexDirection: 'row',
        },
      }}>
      <MuiBox
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '4px',
          paddingBlock: '24px',
          paddingInline: '18px',
          [MediaQuery.desktop]: {
            borderBottom: '1px solid #82807E1A',
            alignItems: 'center',
          },

          ['& svg']: {
            height: '56px',
            width: '56px',
            marginBottom: '12px',
          },
          ['& path']: {
            strokeWidth: '0.75px',
          },

          [MediaQuery.mobile]: {
            padding: '0',
            paddingRight: 'calc(40vw - 110px)',
            borderRight: '1px solid #82807E1A',
            alignItems: 'flex-start',

            ['& svg']: {
              height: '40px',
              width: '40px',
              marginBottom: '12px',
            },
          },
        }}>
        {IconMap[props.iconType]}
        <MuiTypography
          sx={{
            fontWeight: 500,
            fontSize: '24px',
            lineHeight: '30px',
            letterSpacing: '-0.4px',
            textAlign: 'center',
            color: '#2D2D2D',
            [MediaQuery.mobile]: {
              fontWeight: 400,
              fontSize: '17px',
              lineHeight: '23.75px',
            },
          }}>
          {props.name}
        </MuiTypography>
        <MuiTypography
          sx={{
            fontWeight: 500,
            fontSize: '13px',
            lineHeight: '16.25px',
            letterSpacing: '0px',
            textAlign: 'center',
            color: '#2D2D2D',
            opacity: '0.6',

            [MediaQuery.mobile]: {
              fontWeight: 400,
              fontSize: '13px',
              lineHeight: '16.25px',
              letterSpacing: '0px',
              textAlign: 'center',
            },
          }}>
          Property Type
        </MuiTypography>
      </MuiBox>
      {/* <MuiBox
        sx={{
          display: 'flex',
          flexDirection: 'column',
          paddingBlock: '24px',
          [MediaQuery.mobile]: {
            padding: 0,
            justifyContent: 'space-between',
            paddingLeft: '24px',
            alignItems: 'flex-start',
          },
        }}>
        <MuiBox
          sx={{
            display: 'flex',
            gap: '8px',
            alignItems: 'center',
            justifyContent: 'center',
            ['& svg']: {
              height: '40px',
              width: '40px',
            },

            [MediaQuery.mobile]: {
              marginBottom: '8px',
              ['& svg']: {
                height: '24px',
                width: '24px',
              },
            },
          }}>
          <BedroomIcon />
          <MuiTypography
            sx={{
              fontWeight: 400,
              fontSize: '32px',
              lineHeight: '35.2px',
              letterSpacing: '-0.8px',
              textAlign: 'center',
              color: '#2D2D2D',
              [MediaQuery.mobile]: {
                fontSize: '17px',
                fontSize: '17px',
                lineHeight: '23.75px',
                letterSpacing: '-0.4px',
              },
            }}>
            {props.bedrooms}
          </MuiTypography>
        </MuiBox>
        <MuiTypography
          sx={{
            fontWeight: 400,
            fontSize: '17px',
            lineHeight: '23.8px',
            letterSpacing: '-0.4px',
            textAlign: 'center',
            color: '#2D2D2D',
            [MediaQuery.mobile]: {
              fontSize: '15px',
              lineHeight: '18.75px',
            },
          }}>
          Bedroom
        </MuiTypography>
        <MuiTypography
          sx={{
            fontWeight: 400,
            fontSize: '17px',
            lineHeight: '23.8px',
            letterSpacing: '-0.4px',
            textAlign: 'center',
            color: '#2D2D2D',
            [MediaQuery.mobile]: {
              fontSize: '15px',
              lineHeight: '18.75px',
            },
          }}>
          {props.name}
        </MuiTypography>
        <MuiTypography
          sx={{
            fontWeight: 500,
            fontSize: '13px',
            lineHeight: '16.25px',
            letterSpacing: 0,
            textAlign: 'center',
            color: '#2D2D2D',
            opacity: '0.6',
          }}>
          Unit Type
        </MuiTypography>
      </MuiBox> */}
    </MuiBox>
  );
}

function BedroomIcon() {
  return (
    <svg
      width='41'
      height='40'
      viewBox='0 0 41 40'
      fill='none'
      xmlns='http://www.w3.org/2000/svg'>
      <path
        d='M3.5 5.83398L3.5 34.1673'
        stroke='#2D2D2D'
        strokeWidth='1.25'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
      <path
        d='M36.8359 14.167L36.8359 34.167'
        stroke='#2D2D2D'
        strokeWidth='1.25'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
      <path
        d='M3.5 14.167L10.1667 17.5003H36.8333'
        stroke='#2D2D2D'
        strokeWidth='1.25'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
      <path
        d='M3.5 25.834H10.1667M36.8333 25.834H31.8333'
        stroke='#2D2D2D'
        strokeWidth='1.25'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
      <path
        d='M10.1641 17.5V27.5C10.1641 30.2578 10.7396 30.8333 13.4974 30.8333H28.4974C31.2552 30.8333 31.8307 30.2578 31.8307 27.5V17.5'
        stroke='#2D2D2D'
        strokeWidth='1.25'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
      <path
        d='M11.5227 17.5003C11.6564 16.7936 11.867 16.0545 11.8261 15.3289C11.7657 14.2563 11.2283 13.2774 10.3773 12.6898C10.0413 12.4578 9.29999 12.221 8.52113 12.0185C7.38467 11.7232 6.81644 11.5755 6.15237 11.7277C5.68068 11.8359 5.27097 12.0868 4.68524 12.535C4.31708 12.8167 4.133 12.9575 3.97627 13.1574C3.95396 13.1859 3.92959 13.2189 3.90893 13.2485C3.76376 13.457 3.67584 13.6936 3.5 14.167V14.167'
        stroke='#2D2D2D'
        strokeWidth='1.25'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    </svg>
  );
}
